import Spinner from '@carvana/ds-spinner';
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { AvailableOptions, Dashboard, StepName, Summary } from '|/app/services';
import type { Step } from '|/app/services';
import { useBrand } from '|/brand';
import { SevenDayBanner } from '|/components/seven-day-banner';
import {
  DlReconditioningContext,
  FormStateContext,
  NextStepNavigationContextProvider,
  PageEventContextProvider,
  PersonaLifecycleContext,
  type PersonaLifecycleCallbacks,
  PersonaLifecycleProvider,
  UserInitializationContext
} from '|/context';
import useEventHelper from '|/hooks/use-event-helper';
import {
  clearScopedSlotPreference,
  getScopedSlotPreference,
  type SlotPreferenceScope
} from '|/hooks/use-slot-preference';
import type { ReviewSummaryResponse } from '|/services/purchase-api';
import CancelOrderModalListener from './cancel-order-modal-listener';
import styles from './checkout-page.module.css';
import CheckoutAccordion, {
  HeightCoordinatorProvider,
  type AccordionStepData
} from './components/checkout-accordion';
import CheckoutFooter from './components/checkout-footer';
import EditOrderLink from './components/edit-order-link';
import MobileVehicleCard from './components/mobile-vehicle-card';
import SlateVehicleCard, { SlateMobileVehicleCard } from './components/slate-vehicle-card';
import VehicleCard from './components/vehicle-card';
import { getPageEventContext } from './constants/page-event-mappings';
import AutopayCheckoutSummary from './steps/autopay-enrollment/cio/checkout-summary';
import BuyerIdentityCheckoutSummary from './steps/buyer-identity/checkout-summary';
import CompletePurchase from './steps/complete-purchase';
import PaymentCheckoutSummary from './steps/down-payment/cio/checkout-summary';
import TransferSelectSlot from './steps/transfer-select-slot';
import ScheduleCheckoutSummary from './steps/transfer-select-slot/checkout-summary';
import { transformCheckoutStepTitle } from './transform-checkout-step-title';
import useCheckoutVm from './use-checkout-vm';
import { VERIFICATION_STEP_IDS, buildVerificationStepUrlSet } from './verifications/constants';
import VerificationsPanel from './verifications/verifications-panel';
import { StepElement } from '../flow/steps-router';

const FoldLoadingBoundary: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isLoading, loadError } = useContext(FormStateContext);

  if (loadError !== null) {
    throw loadError instanceof Error
      ? loadError
      : new Error('Unexpected error', { cause: loadError });
  }
  return (
    <Suspense
      fallback={
        <div className={styles.foldSuspenseFallback}>
          <Spinner size={48} />
        </div>
      }
    >
      <div className={styles.foldLoadingWrapper}>
        <div
          className={styles.foldLoadingOverlay}
          style={{ opacity: isLoading ? 1 : 0, pointerEvents: isLoading ? 'auto' : 'none' }}
          aria-hidden={!isLoading}
        >
          <Spinner size={48} />
        </div>
        <div style={{ display: isLoading ? 'none' : undefined }}>{children}</div>
      </div>
    </Suspense>
  );
};

interface CheckoutPageProps {
  dashboard: Dashboard | null;
  setDashboard: (d: Dashboard) => void;
  summary: Summary | null;
  setSummary: (s: Summary) => void;
  setAvailableOptions: (a: AvailableOptions) => void;
  isOrderSummaryModalOpen: boolean;
  setIsOrderSummaryModalOpen: (open: boolean) => void;
  /**
   * Rolls the order back to the cart. Rendered above the accordion on desktop; on mobile the
   * link lives in the checkout header instead.
   */
  onEditOrder: () => void;
}

function isPlaceOrderComplete(scheduleStep?: Step, caseStep?: Step): boolean {
  if (scheduleStep?.isEnabled && !scheduleStep.isComplete) return false;
  if (caseStep?.isEnabled && !caseStep.isComplete) return false;
  return true;
}

const FirePageLoadOnMount: React.FC = () => {
  const { sendLoadEvent } = useEventHelper();
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    sendLoadEvent(true);
  }, [sendLoadEvent]);
  return null;
};

// Starts Persona client construction as soon as the dashboard, DL-reconditioning bucketing, and
// brand have all settled, instead of waiting for the identity fold to expand.
const PersonaEagerPreload: React.FC<{ dashboard: Dashboard | null }> = ({ dashboard }) => {
  const { isDlReconditioningTreatment, isSettled } = useContext(DlReconditioningContext);
  // Persona's themeSetId is baked in at construction (persona-client.ts) from brand at that
  // instant, with no rebuild if brand later resolves differently — so this must wait for
  // useBrand().isSettled the same way review-order.tsx does, or an impersonated session reaching
  // this effect before the vehicle-driven brand upgrade lands would permanently construct a
  // Carvana-themed client for what turns out to be a Slate customer.
  const { isSettled: isBrandSettled } = useBrand();
  const { createIfAbsent, getProviderEpoch } = useContext(PersonaLifecycleContext);
  // Keyed on the provider's epoch rather than a plain boolean: a provider teardown (StrictMode's
  // simulated remount in dev) destroys the clients this started, so the preload has to re-arm.
  const preloadedEpochRef = useRef<number | null>(null);

  useEffect(() => {
    if (preloadedEpochRef.current === getProviderEpoch()) return;
    if (!isSettled || !isDlReconditioningTreatment || !isBrandSettled) return;
    // The provider mounts before isAccordionReady, so dashboard can still be null here.
    if (dashboard === null) return;
    preloadedEpochRef.current = getProviderEpoch();

    // Buyer identity verification is universal — every order has a primary buyer, so isEnabled is
    // invariant true for this step — unlike cobuyer below, which needs the isEnabled check.
    createIfAbsent(false, 'eager-preload');
    const hasEnabledCoSignerIdentity = dashboard.steps.some(
      s => s.id === StepName.CoSignerIdentity && s.isEnabled
    );
    if (hasEnabledCoSignerIdentity) {
      createIfAbsent(true, 'eager-preload');
    }
  }, [
    isSettled,
    isDlReconditioningTreatment,
    isBrandSettled,
    dashboard,
    createIfAbsent,
    getProviderEpoch
  ]);

  return null;
};

// Module scope is load-bearing, not stylistic: a type declared inside CheckoutPage's own body gets
// a new identity every render, and React remounts a subtree when its type changes — destroying and
// rebuilding PersonaLifecycleProvider's Persona clients every render (the TXCC-9787 churn).
// Mounts inside the 'Checkout' PageEventContextProvider so useEventHelper resolves page attribution.
const PersonaLifecycleProviderWithEvents: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { trackEvent } = useEventHelper();
  const sendEvent = useCallback<PersonaLifecycleCallbacks['sendEvent']>(
    (name, success, metadata) => {
      trackEvent(name, {
        url: window.location.pathname,
        successEvent: success,
        metadata,
        devicePlatform: 'web'
      });
    },
    [trackEvent]
  );

  return <PersonaLifecycleProvider sendEvent={sendEvent}>{children}</PersonaLifecycleProvider>;
};

function getCheckoutSlotPreferenceScope(
  summary: Summary | null,
  userId: string | undefined
): SlotPreferenceScope | undefined {
  const vehicleId = summary?.vehicle?.id;
  if (!userId || vehicleId == null) return undefined;

  return {
    userId,
    vehicleId,
    purchaseStartedAt: summary?.cartCreatedDateTimeUtc ?? null
  };
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({
  dashboard,
  setDashboard,
  summary,
  setSummary,
  setAvailableOptions,
  setIsOrderSummaryModalOpen,
  onEditOrder
}) => {
  const { brand } = useBrand();
  const isSlate = brand === 'slate';
  const { accessToken } = useContext(UserInitializationContext);
  const slotPreferenceScope = useMemo(
    () => getCheckoutSlotPreferenceScope(summary, accessToken?.user.user_id),
    [summary, accessToken]
  );
  const hasScheduleSlotPreference = useCallback(
    () => getScopedSlotPreference(slotPreferenceScope) != null,
    [slotPreferenceScope]
  );
  const clearScheduleSlotPreference = useCallback(() => {
    clearScopedSlotPreference();
  }, []);

  const {
    activeStepUrl,
    exitingStepUrl,
    setActiveStepUrl,
    handleStepComplete,
    handleToggle,
    ensureInProgress,
    isRefreshing,
    vehicleSummary,
    isSlotRetainedAfterRollback
  } = useCheckoutVm(
    dashboard,
    summary,
    setDashboard,
    setSummary,
    setAvailableOptions,
    hasScheduleSlotPreference,
    clearScheduleSlotPreference
  );

  const section2Steps = useMemo<AccordionStepData[]>(() => {
    if (!dashboard) return [];

    const allSection2 = [...dashboard.steps]
      .filter(s => s.section === 2 && s.isEnabled)
      .sort((a, b) => a.order - b.order);

    const verifSteps = allSection2.filter(s => VERIFICATION_STEP_IDS.has(s.id));
    const verificationIsComplete = verifSteps.length > 0 && verifSteps.every(s => s.isComplete);

    const scheduleStep = allSection2.find(s => s.id === StepName.Schedule);
    const caseStep = allSection2.find(s => s.id === StepName.CaseSubmission);
    const slotPref = getScopedSlotPreference(slotPreferenceScope);

    const result: AccordionStepData[] = [];
    let verificationEntryAdded = false;
    let completePurchaseEmitted = false;

    for (const step of allSection2) {
      // Insurance is rendered full-screen outside the accordion.
      if (step.id === StepName.Insurance) continue;

      // 1. Verification grouping (unchanged)
      if (VERIFICATION_STEP_IDS.has(step.id)) {
        if (!verificationEntryAdded) {
          result.push({
            url: '_verification',
            title: 'Final details',
            isComplete: verificationIsComplete,
            summary: verificationIsComplete ? 'All final details complete' : undefined
          });
          verificationEntryAdded = true;
        }
        continue;
      }

      // 2. Schedule → emit transfer-select-slot + maybe complete-purchase
      if (step.id === StepName.Schedule) {
        result.push({
          url: 'transfer-select-slot',
          title: transformCheckoutStepTitle(step.id, step.name, summary ?? null),
          isComplete: slotPref != null,
          summaryContent: slotPref ? (
            <ScheduleCheckoutSummary summary={summary} slotPreference={slotPref} />
          ) : undefined
        });

        // If no CaseSubmission follows, emit complete-purchase here
        if (!caseStep) {
          result.push({
            url: 'complete-purchase',
            title: 'Place order',
            isComplete: isPlaceOrderComplete(scheduleStep, caseStep)
          });
          completePurchaseEmitted = true;
        }
        continue;
      }

      // 3. CaseSubmission → emit complete-purchase (if not already)
      if (step.id === StepName.CaseSubmission) {
        if (!completePurchaseEmitted) {
          result.push({
            url: 'complete-purchase',
            title: 'Place order',
            isComplete: isPlaceOrderComplete(scheduleStep, caseStep)
          });
          completePurchaseEmitted = true;
        }
        continue;
      }

      // 4. Regular step
      const stepData: AccordionStepData = {
        url: step.url,
        title: transformCheckoutStepTitle(step.id, step.name, summary ?? null),
        isComplete: step.isComplete
      };

      if (step.isComplete) {
        if (step.id === StepName.Payment) {
          stepData.summaryContent = <PaymentCheckoutSummary summary={summary} />;
        } else if (step.id === StepName.Autopay) {
          stepData.summaryContent = <AutopayCheckoutSummary summary={summary} />;
        } else if (step.id === StepName.BuyerIdentity) {
          stepData.summaryContent = <BuyerIdentityCheckoutSummary />;
        } else if (step.id === StepName.CoSignerIdentity) {
          stepData.summaryContent = <BuyerIdentityCheckoutSummary isCobuyer />;
        }
      }

      result.push(stepData);
    }

    return result;
  }, [dashboard, summary, slotPreferenceScope]);

  const verificationStepUrls = useMemo(
    () => (dashboard ? buildVerificationStepUrlSet(dashboard.steps) : new Set<string>()),
    [dashboard]
  );

  const editPaymentMethod = useCallback(() => {
    const paymentStepUrl = dashboard?.steps.find(step => step.id === StepName.Payment)?.url;

    if (paymentStepUrl) {
      setActiveStepUrl(paymentStepUrl);
    }
  }, [dashboard, setActiveStepUrl]);

  const stepChildren = useMemo<Record<string, React.ReactNode>>(() => {
    if (!dashboard) return {};

    const entries: [string, React.ReactNode][] = [];

    for (const stepData of section2Steps) {
      if (stepData.url === '_verification') {
        const verifSteps = dashboard.steps.filter(
          s => VERIFICATION_STEP_IDS.has(s.id) && s.section === 2 && s.isEnabled
        );
        entries.push([
          '_verification',
          <VerificationsPanel
            key="_verification"
            verificationSteps={verifSteps}
            activeStepUrl={activeStepUrl ?? ''}
            setActiveStepUrl={setActiveStepUrl}
            onStepComplete={handleStepComplete}
            summary={summary as ReviewSummaryResponse | null}
            isSlotRetainedAfterRollback={isSlotRetainedAfterRollback}
            setDashboard={setDashboard}
            setSummary={setSummary}
          />
        ]);
        continue;
      }

      if (stepData.url === 'transfer-select-slot') {
        entries.push([
          'transfer-select-slot',
          <PageEventContextProvider
            key="transfer-select-slot"
            value={getPageEventContext('transfer-select-slot')}
          >
            <NextStepNavigationContextProvider
              value={{ handleNextStep: handleStepComplete, ensureInProgress }}
            >
              <FoldLoadingBoundary>
                <TransferSelectSlot slotPreferenceScope={slotPreferenceScope} />
              </FoldLoadingBoundary>
            </NextStepNavigationContextProvider>
          </PageEventContextProvider>
        ]);
        continue;
      }

      if (stepData.url === 'complete-purchase') {
        const hasScheduleStep = dashboard.steps.some(
          s => s.id === StepName.Schedule && s.isEnabled
        );
        entries.push([
          'complete-purchase',
          <PageEventContextProvider
            key="complete-purchase"
            value={getPageEventContext('complete-purchase')}
          >
            <NextStepNavigationContextProvider
              value={{ handleNextStep: handleStepComplete, ensureInProgress }}
            >
              <FoldLoadingBoundary>
                <CompletePurchase
                  hasScheduleStep={hasScheduleStep}
                  onStepComplete={handleStepComplete}
                  setActiveStepUrl={setActiveStepUrl}
                  editPaymentMethod={editPaymentMethod}
                  slotPreferenceScope={slotPreferenceScope}
                  marketing={summary?.marketing}
                  summary={summary}
                />
              </FoldLoadingBoundary>
            </NextStepNavigationContextProvider>
          </PageEventContextProvider>
        ]);
        continue;
      }

      const step = dashboard.steps.find(s => s.url === stepData.url);
      if (!step) {
        entries.push([stepData.url, null]);
        continue;
      }

      entries.push([
        stepData.url,
        <PageEventContextProvider key={stepData.url} value={getPageEventContext(stepData.url)}>
          <NextStepNavigationContextProvider
            value={{ handleNextStep: handleStepComplete, ensureInProgress }}
          >
            <FoldLoadingBoundary>
              <StepElement
                key={stepData.url}
                step={step}
                GetDashboardAndRouteToNextStep={() => {
                  handleStepComplete();
                }}
              />
            </FoldLoadingBoundary>
          </NextStepNavigationContextProvider>
        </PageEventContextProvider>
      ]);
    }

    return Object.fromEntries(entries);
  }, [
    dashboard,
    setDashboard,
    section2Steps,
    handleStepComplete,
    activeStepUrl,
    setActiveStepUrl,
    ensureInProgress,
    summary,
    isSlotRetainedAfterRollback,
    setSummary,
    slotPreferenceScope,
    editPaymentMethod
  ]);

  const handleViewOrderDetails = () => {
    setIsOrderSummaryModalOpen(true);
  };

  const isAccordionReady = dashboard !== null && summary !== null && activeStepUrl !== null;

  return (
    <PageEventContextProvider
      value={{ pageName: 'Checkout', pageSubName: null, sectionName: 'Checkout' }}
    >
      <FirePageLoadOnMount />
      <div className={styles.background}>
        <div className={styles.page}>
          <main className={styles.main}>
            {/* Edit order takes the place of a "Checkout" heading on desktop; on mobile the
                link lives in the header instead. */}
            <div className={styles.desktopEditOrder}>
              <EditOrderLink onClick={onEditOrder} data-analytics-id="pp_checkout_edit_order" />
            </div>
            {/* Wraps both branches so the Persona clients it owns survive the isAccordionReady
                flip as well as every fold's own mount/unmount cycle. */}
            <PersonaLifecycleProviderWithEvents>
              <PersonaEagerPreload dashboard={dashboard} />
              {isAccordionReady ? (
                <HeightCoordinatorProvider debounceMs={50}>
                  <CheckoutAccordion
                    steps={section2Steps}
                    activeStepUrl={activeStepUrl}
                    exitingStepUrl={exitingStepUrl}
                    onToggle={handleToggle}
                    stepChildren={stepChildren}
                    verificationStepUrls={verificationStepUrls}
                    isRefreshing={isRefreshing}
                  />
                </HeightCoordinatorProvider>
              ) : (
                <div className={styles.accordionPlaceholder}>
                  <Spinner size={48} />
                </div>
              )}
            </PersonaLifecycleProviderWithEvents>
          </main>
          <aside className={styles.aside}>
            {vehicleSummary ? (
              <>
                <div className={styles.vehicleCardDesktop}>
                  {isSlate ? (
                    <SlateVehicleCard
                      summary={vehicleSummary}
                      onViewOrderDetails={handleViewOrderDetails}
                    />
                  ) : (
                    <div className={styles.vehicleCardWithBanner}>
                      <VehicleCard
                        summary={vehicleSummary}
                        onViewOrderDetails={handleViewOrderDetails}
                      />
                      <SevenDayBanner analyticsId="pp_checkout_vehicle_card_seven_day_link" />
                    </div>
                  )}
                </div>
                <div className={styles.vehicleCardMobile}>
                  {isSlate ? (
                    /* Slate's checkout header carries no edit link, so this module is the
                       only place mobile can offer one (D-56). */
                    <SlateMobileVehicleCard
                      summary={vehicleSummary}
                      onViewOrderDetails={handleViewOrderDetails}
                      onEditOrder={onEditOrder}
                    />
                  ) : (
                    <MobileVehicleCard
                      summary={vehicleSummary}
                      onViewOrderDetails={handleViewOrderDetails}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className={styles.vehicleCardDesktop}>
                <div className={styles.vehicleCardPlaceholder}>
                  <Spinner size="lg" />
                </div>
              </div>
            )}
          </aside>
        </div>
        {isAccordionReady && <CheckoutFooter />}
      </div>
      <CancelOrderModalListener />
    </PageEventContextProvider>
  );
};

export default CheckoutPage;
